import VueCookies from "vue-cookies";
import { logger } from "@/services/log.js";
import { createSessionId, getSessionId } from "./requestTracking";

// Diagnostic-only logging for unexpected redirects to /validatetelephone.
// ห้ามเปลี่ยน behavior ของ auth flow — ใช้เพื่อเก็บหลักฐานเท่านั้น
// browserId อยู่ใน cookie + localStorage (ระดับ cookie jar), pageLoadId ใหม่ทุกครั้งที่โหลดหน้า

const BROWSER_ID_KEY = "crm_diag_bid";
const BROWSER_ID_MAX_AGE = "1y";
const VISIBLE_SUFFIX_LENGTH = 4;
const SENSITIVE_QUERY_KEYS = [
  "member",
  "token",
  "line_token",
  "member_token",
  "tel",
  "email",
  "userId"
];

const pageLoadId = createSessionId();
const pageLoadStartedAt = Date.now();
let bootState = null;

const maskValue = value => {
  if (value === null || value === undefined || value === "") return "";
  const text = String(value);
  if (text.length <= VISIBLE_SUFFIX_LENGTH) return "*".repeat(text.length);
  return `${"*".repeat(text.length - VISIBLE_SUFFIX_LENGTH)}${text.slice(
    -VISIBLE_SUFFIX_LENGTH
  )}`;
};

const readLocalStorage = key => {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const writeLocalStorage = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    // storage ถูกปิด (private mode / blocked) — ค่า storageId จะว่างใน log
  }
};

const resolveBrowserIds = () => {
  const cookieId = VueCookies.get(BROWSER_ID_KEY) || "";
  const storageId = readLocalStorage(BROWSER_ID_KEY) || "";
  const browserId = cookieId || storageId || createSessionId();

  if (!cookieId) VueCookies.set(BROWSER_ID_KEY, browserId, BROWSER_ID_MAX_AGE);
  if (!storageId) writeLocalStorage(BROWSER_ID_KEY, browserId);

  return {
    browserId,
    hadBrowserIdCookie: Boolean(cookieId),
    hadBrowserIdStorage: Boolean(storageId)
  };
};

const getMaskedQuery = () => {
  const params = new URLSearchParams(window.location.search);
  const masked = {};
  params.forEach((value, key) => {
    masked[key] = SENSITIVE_QUERY_KEYS.includes(key) ? maskValue(value) : value;
  });
  return masked;
};

const getCookieState = () => ({
  hasToken: VueCookies.isKey("token"),
  hasTelephoneLogin: VueCookies.isKey("telephone_login"),
  telephoneLoginMasked: maskValue(VueCookies.get("telephone_login")),
  hasUserProfileToken: VueCookies.isKey("user_profile_token")
});

const getTrackingIds = () => ({
  browserId: bootState?.browserId || VueCookies.get(BROWSER_ID_KEY) || "",
  pageLoadId,
  sessionId: getSessionId(),
  host: window.location.host
});

// เรียกครั้งเดียวใน main.js หลังกำหนด socialProvider/socialID แล้ว
const captureBootAuthState = config => {
  try {
    bootState = {
      ...resolveBrowserIds(),
      isLoginLine: config?.isLoginLine,
      socialProvider: config?.socialProvider || "",
      socialIdMasked: maskValue(config?.socialID),
      cookies: getCookieState(),
      referrer: document.referrer || "",
      cookieEnabled: navigator.cookieEnabled,
      standalone: Boolean(window.navigator.standalone)
    };
  } catch (error) {
    console.error("captureBootAuthState error:", error);
  }
};

const logAuthDiagnostic = (event, detail = {}) => {
  try {
    const note = {
      event,
      ...getTrackingIds(),
      path: window.location.pathname,
      query: getMaskedQuery(),
      msSinceLoad: Date.now() - pageLoadStartedAt,
      boot: bootState,
      current: getCookieState(),
      ...detail
    };

    logger({
      response: { config: { url: event, method: "AUTH_DIAG" }, status: 0 },
      action: {
        id: 3,
        action: `auth diagnostic: ${event}`,
        note: JSON.stringify(note)
      },
      includeAuth: false
    });
  } catch (error) {
    console.error("logAuthDiagnostic error:", error);
  }
};

export { captureBootAuthState, getTrackingIds, logAuthDiagnostic, maskValue };
